import { useState, useEffect } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useAuth } from '@/hooks/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

// Google Icon SVG component
const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

export const SignupPage = () => {
  const { t } = useI18n();
  const { lang } = useParams<{ lang: string }>();
  useDocumentLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { signup, handleGoogleCallback, getGoogleOAuthUrl, isAuthenticated, isLoading: authLoading } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate(`/${lang}/admin`);
    }
  }, [isAuthenticated, authLoading, navigate, lang]);

  // Handle OAuth callback code from URL
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      setIsGoogleLoading(true);
      handleGoogleCallback(code)
        .then(() => {
          navigate(`/${lang}/admin`);
        })
        .catch((err) => {
          setError(err.message || 'OAuth signup failed');
          setIsGoogleLoading(false);
          // Clear the code from URL
          navigate(`/${lang}/signup`, { replace: true });
        });
    }
  }, [searchParams, handleGoogleCallback, navigate, lang]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t('signup.passwordsDoNotMatch'));
      return;
    }

    // Validate password length
    if (password.length < 8) {
      setError(t('signup.passwordTooShort'));
      return;
    }

    setIsSubmitting(true);

    try {
      await signup(email, password, name);
      navigate(`/${lang}/admin`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignup = async () => {
    setError(null);
    setIsGoogleLoading(true);

    try {
      // Build the callback URL for this page
      const callbackUrl = `${window.location.origin}/${lang}/signup`;
      const authUrl = await getGoogleOAuthUrl(callbackUrl);
      // Redirect to Google OAuth
      window.location.href = authUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initiate Google signup');
      setIsGoogleLoading(false);
    }
  };

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-12 h-12 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('signup.title')}
            </h1>
            <p className="text-gray-600">
              {t('signup.subtitle')}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Google OAuth Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full mb-6 h-12 text-base font-medium border-gray-300 hover:bg-gray-50"
            onClick={handleGoogleSignup}
            disabled={isGoogleLoading || isSubmitting}
          >
            {isGoogleLoading ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <GoogleIcon />
            )}
            <span className="ml-3">
              {isGoogleLoading ? t('signup.creatingAccount') : t('signup.continueWithGoogle')}
            </span>
          </Button>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">{t('signup.orContinueWith')}</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-gray-700">
                {t('signup.name')}
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('signup.namePlaceholder')}
                required
                className="mt-2 h-12"
                disabled={isSubmitting || isGoogleLoading}
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-700">
                {t('signup.email')}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('signup.emailPlaceholder')}
                required
                className="mt-2 h-12"
                disabled={isSubmitting || isGoogleLoading}
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-700">
                {t('signup.password')}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('signup.passwordPlaceholder')}
                required
                minLength={8}
                className="mt-2 h-12"
                disabled={isSubmitting || isGoogleLoading}
              />
              <p className="mt-1 text-xs text-gray-500">{t('signup.passwordHint')}</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-gray-700">
                {t('signup.confirmPassword')}
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('signup.confirmPasswordPlaceholder')}
                required
                className="mt-2 h-12"
                disabled={isSubmitting || isGoogleLoading}
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base font-medium bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
              disabled={isSubmitting || isGoogleLoading}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  {t('signup.creatingAccount')}
                </>
              ) : (
                t('signup.createAccount')
              )}
            </Button>
          </form>

          {/* Sign In Link */}
          <div className="mt-6 text-center text-sm text-gray-600">
            {t('signup.haveAccount')}{' '}
            <Link
              to={`/${lang}/login`}
              className="text-blue-600 hover:text-blue-700 font-semibold"
            >
              {t('signup.signIn')}
            </Link>
          </div>
        </div>

        {/* Terms */}
        <p className="mt-6 text-center text-xs text-gray-500">
          {t('signup.termsNotice')}{' '}
          <Link to={`/${lang}/terms`} className="underline hover:text-gray-700">
            {t('signup.termsOfService')}
          </Link>{' '}
          {t('signup.and')}{' '}
          <Link to={`/${lang}/privacy`} className="underline hover:text-gray-700">
            {t('signup.privacyPolicy')}
          </Link>
        </p>
      </div>
    </div>
  );
};
